import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import {
    Constraint_Stacks_Bool_Exp,
    useGetBusCapacityCostsQuery
} from "generated/graphql";
import { loader } from "graphql.macro";
import { getHasuraDataAndConvertToNiraType } from "graphql/helpers/queryHelpers";
import React, { useMemo } from "react";
import { BusId, NiraBusId } from "types/busType";
import { isPricedConstraintConfig } from "types/componentConfigPerRegion";
import { convertHasuraConstraintsToConstraints } from "types/constraintType";
import { ScopeName } from "types/scope";
import { ScopeViewType } from "types/scopeViewType";
import { CapacityCostsGraphRenderer } from "../shared/CostGraphsSection/CostGraphsSection";

const GET_CONSTRAINTS = loader("src/graphql/getConstraints.graphql");

type BusCapacityCostsGraphProps = {
    readonly busId: BusId;
    readonly busName?: string;
    readonly niraBusId: NiraBusId;
    readonly scope: ScopeName;
    readonly scopeView: ScopeViewType;
    readonly includesPreExisting: boolean;
    readonly projectEnergySize?: number;
    readonly projectChargingSize?: number;
    readonly unlockBusLoading?: boolean;
};

/**
 * Main component - handles data fetching and loading states
 */
const BusCapacityCostsGraph: React.FC<BusCapacityCostsGraphProps> = (props) => {
    const {
        busId,
        busName,
        niraBusId,
        scope,
        scopeView,
        includesPreExisting,
        unlockBusLoading
    } = props;

    const { componentConfig } = useMapViewRoutingMetadata();

    const { data, loading, error } = useGetBusCapacityCostsQuery({
        variables: {
            busId,
            scope,
            scopeView,
            includesPreExisting
        },
        skip: unlockBusLoading
    });

    const getConstraintsQueryVariables: Constraint_Stacks_Bool_Exp = useMemo(
        () =>
            includesPreExisting
                ? {
                      nira_bus_id: { _eq: niraBusId },
                      scope: { _eq: scope },
                      scope_view: { _eq: scopeView }
                  }
                : {
                      nira_bus_id: { _eq: niraBusId },
                      scope: { _eq: scope },
                      scope_view: { _eq: scopeView },
                      is_pre_existing: { _eq: false }
                  },
        [niraBusId, scope, scopeView, includesPreExisting]
    );

    const maybeConstraints = getHasuraDataAndConvertToNiraType(
        GET_CONSTRAINTS,
        convertHasuraConstraintsToConstraints,
        { constraintsWhereClause: getConstraintsQueryVariables },
        unlockBusLoading || false
    );

    // Early return if region doesn't support priced constraints
    if (!isPricedConstraintConfig(componentConfig)) {
        return null;
    }

    const isLoading =
        loading || maybeConstraints === "loading" || !!unlockBusLoading;

    if (!isLoading) {
        if (error || !data) {
            return null;
        }

        if (data.capacity_costs.length === 0) {
            return null;
        }

        // Validate that constraints data matches the current bus
        if (maybeConstraints.length > 0) {
            const firstConstraint = maybeConstraints[0];
            if (firstConstraint.busId !== busId) {
                return null;
            }
        }
    }

    return (
        <CapacityCostsGraphRenderer
            exportId={busId}
            exportName={busName}
            scope={scope}
            scopeView={scopeView}
            projectEnergySize={props.projectEnergySize}
            projectChargingSize={props.projectChargingSize}
            capacityCostsData={!isLoading && data ? data.capacity_costs : []}
            constraints={!isLoading ? maybeConstraints : []}
            isLoading={isLoading}
        />
    );
};

export default BusCapacityCostsGraph;
